import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentOutline = ({ 
  sections, 
  selectedSection, 
  onSectionSelect, 
  onAddSection, 
  onDeleteSection,
  onReorderSections 
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleAddSection = () => {
    if (newSectionTitle?.trim()) {
      onAddSection({
        id: Date.now()?.toString(),
        title: newSectionTitle,
        level: 1,
        subsections: []
      });
      setNewSectionTitle('');
      setShowAddSection(false);
    }
  };

  const renderSection = (section, level = 0) => {
    const isExpanded = expandedSections?.[section?.id];
    const hasSubsections = section?.subsections && section?.subsections?.length > 0;
    const isSelected = selectedSection === section?.id;

    return (
      <div key={section?.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        <div
          className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-micro group ${
            isSelected 
              ? 'bg-primary text-primary-foreground' 
              : 'hover:bg-muted text-foreground'
          }`}
          onClick={() => onSectionSelect(section?.id)}
        >
          <div className="flex items-center space-x-2 flex-1">
            {hasSubsections && (
              <Button
                variant="ghost"
                size="xs"
                onClick={(e) => {
                  e?.stopPropagation();
                  toggleSection(section?.id);
                }}
                iconName={isExpanded ? "ChevronDown" : "ChevronRight"}
                className="p-0 h-4 w-4"
              />
            )}
            
            <Icon 
              name={level === 0 ? "FileText" : "Hash"} 
              size={14} 
              className={isSelected ? "text-primary-foreground" : "text-muted-foreground"}
            />
            
            <span className={`text-sm font-medium ${
              level === 0 ? 'font-semibold' : 'font-normal'
            }`}>
              {section?.title}
            </span>
          </div>

          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-micro">
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e?.stopPropagation();
                onDeleteSection(section?.id);
              }}
              iconName="Trash2"
              className="p-0 h-4 w-4 text-destructive hover:text-destructive"
            />
          </div>
        </div>
        {hasSubsections && isExpanded && (
          <div className="mt-1">
            {section?.subsections?.map(subsection => 
              renderSection(subsection, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-64 bg-card border-r border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Document Outline</h3>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setShowAddSection(true)}
            iconName="Plus"
            title="Add Section"
          />
        </div>
      </div>
      {/* Add Section Form */}
      {showAddSection && (
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Section title..."
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e?.target?.value)}
              className="w-full px-2 py-1 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
              onKeyDown={(e) => {
                if (e?.key === 'Enter') handleAddSection();
                if (e?.key === 'Escape') setShowAddSection(false);
              }}
              autoFocus
            />
            <div className="flex items-center space-x-1">
              <Button
                variant="default"
                size="xs"
                onClick={handleAddSection}
                disabled={!newSectionTitle?.trim()}
              >
                Add
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setShowAddSection(false);
                  setNewSectionTitle('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Sections List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-1">
          {sections?.map(section => renderSection(section))}
        </div>

        {sections?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No sections yet</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddSection(true)}
              iconName="Plus"
              iconPosition="left"
              className="mt-2"
            >
              Add First Section
            </Button>
          </div>
        )}
      </div>
      {/* Quick Navigation */}
      <div className="border-t border-border p-4">
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            iconName="Search"
            iconPosition="left"
            className="justify-start"
          >
            Search Sections
          </Button>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            iconName="ArrowUp"
            iconPosition="left"
            className="justify-start"
          >
            Go to Top
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentOutline;